import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  ArrowLeft, 
  Type, 
  Volume2, 
  Paintbrush, 
  Eye,
  CheckCircle,
  Info,
  Zap,
  Mic,
  Palette,
  Monitor
} from 'lucide-react';
import useAutoScroll from '../../hooks/useAutoScroll';

// Types
interface CompetitionMode {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  features: string[];
  difficulty: 'easy' | 'medium' | 'hard';
  recommendedFor: string[];
  color: string;
}

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  color: white;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 10px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
`;

const Title = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0;
  color: #ffd700;
`;

const Description = styled.p`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  line-height: 1.5;
`;

const ModesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 25px;
  margin-bottom: 30px;
`;

const ModeCard = styled.div<{ isSelected: boolean; color: string }>`
  background: ${props => props.isSelected 
    ? 'rgba(255, 255, 255, 0.2)' 
    : 'rgba(255, 255, 255, 0.05)'
  };
  border: 2px solid ${props => props.isSelected 
    ? props.color 
    : 'rgba(255, 255, 255, 0.1)'
  };
  border-radius: 20px;
  padding: 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-8px);
    background: rgba(255, 255, 255, 0.15);
    border-color: ${props => props.color};
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.3);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.color};
    opacity: ${props => props.isSelected ? 1 : 0.3};
    transition: all 0.3s ease;
  }
`;

const ModeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
`;

const ModeIcon = styled.div<{ color: string }>`
  font-size: 2.5rem;
  color: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModeInfo = styled.div`
  flex: 1;
`;

const ModeName = styled.h3`
  font-size: 1.4rem;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: white;
`;

const ModeDescription = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  line-height: 1.4;
`;

const ModeFeatures = styled.div`
  margin: 20px 0;
`;

const FeaturesTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #ffd700;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const FeatureItem = styled.li`
  background: rgba(255, 255, 255, 0.1);
  padding: 6px 12px;
  border-radius: 15px;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const ModeFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const DifficultyBadge = styled.div<{ difficulty: 'easy' | 'medium' | 'hard' }>`
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  background: ${props => {
    switch (props.difficulty) {
      case 'easy': return 'rgba(76, 175, 80, 0.3)';
      case 'medium': return 'rgba(255, 152, 0, 0.3)';
      case 'hard': return 'rgba(244, 67, 54, 0.3)';
      default: return 'rgba(255, 255, 255, 0.1)';
    }
  }};
  color: ${props => {
    switch (props.difficulty) {
      case 'easy': return '#4caf50';
      case 'medium': return '#ff9800';
      case 'hard': return '#f44336';
      default: return 'white';
    }
  }};
  border: 1px solid ${props => {
    switch (props.difficulty) {
      case 'easy': return 'rgba(76, 175, 80, 0.5)';
      case 'medium': return 'rgba(255, 152, 0, 0.5)';
      case 'hard': return 'rgba(244, 67, 54, 0.5)';
      default: return 'rgba(255, 255, 255, 0.2)';
    }
  }};
`;

const RecommendedFor = styled.div`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  text-align: right;
  max-width: 120px;
`;

const SelectionIndicator = styled.div<{ isSelected: boolean }>`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${props => props.isSelected ? '#4caf50' : 'rgba(255, 255, 255, 0.1)'};
  border: 2px solid ${props => props.isSelected ? '#4caf50' : 'rgba(255, 255, 255, 0.3)'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  ${props => props.isSelected && `
    animation: pulse 2s infinite;
    
    @keyframes pulse {
      0% { transform: scale(1); }
      50% { transform: scale(1.1); }
      100% { transform: scale(1); }
    }
  `}
`;

const ContinueButton = styled.button<{ disabled: boolean }>`
  background: ${props => props.disabled 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'linear-gradient(45deg, #4caf50, #8bc34a)'
  };
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 25px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  align-self: center;
  margin-top: 20px;
  
  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 10px 25px rgba(76, 175, 80, 0.4);
  }
  
  &:disabled {
    opacity: 0.5;
  }
`;

const ModeSelection: React.FC<{
  onSelect: (mode: string) => void;
  onBack: () => void;
}> = ({ onSelect, onBack }) => {
  const [selectedMode, setSelectedMode] = useState<string>('');
  const { scrollToContinueButton } = useAutoScroll();

  const competitionModes: CompetitionMode[] = [
    {
      id: 'text',
      name: 'Text Input',
      icon: <Type />,
      description: 'Type your answers using keyboard input. Perfect for detailed explanations and precise responses.',
      features: ['Fast typing', 'Clear communication', 'Easy editing', 'Universal compatibility'],
      difficulty: 'easy',
      recommendedFor: ['Students', 'Fast typers', 'Precise answers'],
      color: '#4caf50'
    },
    {
      id: 'voice',
      name: 'Voice Input',
      icon: <Volume2 />,
      description: 'Speak your answers using voice recognition. Ideal for natural language and quick responses.',
      features: ['Natural speech', 'Quick responses', 'Hands-free', 'AI transcription'],
      difficulty: 'medium',
      recommendedFor: ['Public speakers', 'Natural communicators', 'Hands-free users'],
      color: '#2196f3'
    },
    {
      id: 'drawing',
      name: 'Drawing',
      icon: <Paintbrush />,
      description: 'Draw or sketch your answers. Perfect for visual problems, diagrams, and creative responses.',
      features: ['Visual expression', 'Creative answers', 'Diagram support', 'Touch-friendly'],
      difficulty: 'medium',
      recommendedFor: ['Artists', 'Visual learners', 'Creative thinkers'],
      color: '#ff9800'
    },
    {
      id: 'modeling',
      name: 'Visual Modeling',
      icon: <Eye />,
      description: 'Create 3D models and visual representations. Advanced mode for complex spatial problems.',
      features: ['3D modeling', 'Spatial visualization', 'Advanced tools', 'Professional grade'],
      difficulty: 'hard',
      recommendedFor: ['Engineers', 'Designers', 'Advanced users'],
      color: '#9c27b0'
    }
  ];

  const handleModeSelect = (modeId: string) => {
    setSelectedMode(modeId);
    
    // Auto-scroll to continue button after mode selection
    setTimeout(() => {
      scrollToContinueButton({ 
        behavior: 'smooth', 
        block: 'center',
        offset: 50 
      });
    }, 200);
  };

  const handleContinue = () => {
    if (selectedMode) {
      onSelect(selectedMode);
    }
  };

  const getDifficultyDescription = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'Beginner';
      case 'medium': return 'Intermediate';
      case 'hard': return 'Advanced';
      default: return 'Unknown';
    }
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={onBack}>
          <ArrowLeft size={24} />
        </BackButton>
        <div>
          <Title>Choose Competition Mode</Title>
          <Description>
            Select how you want to input your answers during the competition. 
            Each mode offers different advantages and is suited for different types of questions.
          </Description>
        </div>
      </Header>

      <ModesGrid>
        {competitionModes.map((mode) => (
          <ModeCard
            key={mode.id}
            isSelected={selectedMode === mode.id}
            color={mode.color}
            onClick={() => handleModeSelect(mode.id)}
          >
            <SelectionIndicator isSelected={selectedMode === mode.id}>
              {selectedMode === mode.id && <CheckCircle size={16} color="white" />}
            </SelectionIndicator>

            <ModeHeader>
              <ModeIcon color={mode.color}>
                {mode.icon}
              </ModeIcon>
              <ModeInfo>
                <ModeName>{mode.name}</ModeName>
                <ModeDescription>{mode.description}</ModeDescription>
              </ModeInfo>
            </ModeHeader>

            <ModeFeatures>
              <FeaturesTitle>
                <Zap size={16} />
                Key Features
              </FeaturesTitle>
              <FeaturesList>
                {mode.features.map((feature, index) => (
                  <FeatureItem key={index}>{feature}</FeatureItem>
                ))}
              </FeaturesList>
            </ModeFeatures>

            <ModeFooter>
              <DifficultyBadge difficulty={mode.difficulty}>
                {getDifficultyDescription(mode.difficulty)}
              </DifficultyBadge>
              <RecommendedFor>
                Best for: {mode.recommendedFor.join(', ')}
              </RecommendedFor>
            </ModeFooter>
          </ModeCard>
        ))}
      </ModesGrid>

      <ContinueButton
        id="continue-to-stake-button"
        disabled={!selectedMode}
        onClick={handleContinue}
        className="continue-button"
      >
        Continue to Stake Setting
      </ContinueButton>
    </Container>
  );
};

export default ModeSelection;
